import { safeRandomUUID } from "@/utils/id";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, any>;
  userId?: string;
  sessionId?: string;
}

class Logger {
  private sessionId: string;
  private userId?: string;
  private logBuffer: LogEntry[] = [];
  private readonly maxBufferSize = 100;
  private isDevelopment = import.meta.env.DEV;

  constructor() {
    this.sessionId = safeRandomUUID();
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  clearUserId() {
    this.userId = undefined;
  }

  private createLogEntry(level: LogLevel, message: string, context?: Record<string, any>): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      userId: this.userId,
      sessionId: this.sessionId,
    };
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }
  }

  private formatMessage(entry: LogEntry): string {
    const prefix = {
      debug: "[DEBUG]",
      info: "[INFO]",
      warn: "[WARN]",
      error: "[ERROR]",
    }[entry.level];

    let msg = `${prefix} ${entry.message}`;
    if (entry.context) {
      msg += ` | Context: ${JSON.stringify(entry.context)}`;
    }
    if (entry.userId) {
      msg += ` | User: ${entry.userId}`;
    }
    return msg;
  }

  debug(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry("debug", message, context);
    this.addToBuffer(entry);
    if (this.isDevelopment) {
      console.debug(this.formatMessage(entry));
    }
  }

  info(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry("info", message, context);
    this.addToBuffer(entry);
    if (this.isDevelopment) {
      console.info(this.formatMessage(entry));
    }
  }

  warn(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry("warn", message, context);
    this.addToBuffer(entry);
    if (this.isDevelopment) {
      console.warn(this.formatMessage(entry));
    }
  }

  error(message: string, error?: Error, context?: Record<string, any>) {
    const entry = this.createLogEntry("error", message, {
      ...context,
      error: error?.message,
      stack: error?.stack,
    });
    this.addToBuffer(entry);
    console.error(this.formatMessage(entry), error);
  }

  getRecentLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logBuffer.filter((entry) => entry.level === level);
    }
    return [...this.logBuffer];
  }

  clearLogs() {
    this.logBuffer = [];
  }

  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }
}

export const logger = new Logger();

export const logMeetingEvent = (event: string, meetingId: string, details?: Record<string, any>) => {
  logger.info(`Meeting Event: ${event}`, { meetingId, ...details });
};

export const logWebRTCEvent = (event: string, peerId?: string, details?: Record<string, any>) => {
  logger.info(`WebRTC Event: ${event}`, { peerId, ...details });
};

export const logAuthEvent = (event: string, details?: Record<string, any>) => {
  logger.info(`Auth Event: ${event}`, details);
};

export const logError = (component: string, error: Error, context?: Record<string, any>) => {
  logger.error(`Error in ${component}`, error, context);
};

window.addEventListener("error", (event) => {
  logger.error("Unhandled Error", event.error, {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  logger.error("Unhandled Promise Rejection", reason, {
    reason: event.reason,
  });
});
